import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";

import type { Branding } from "@lagoon/config/branding";
import { Button, buttonVariants, LagoonLockup, Mono, SplashLayout, Text } from "@lagoon/ui";

import { api } from "../api.js";
import { Failure, Notice } from "../components/feedback.js";
import type { OrgRole } from "../orgs.js";

/**
 * The screen at the end of an invitation email.
 *
 * Three things about this flow are deliberate and easy to undo by accident.
 *
 * **Accepting is a button, not a page load.** The platform's accept route is a
 * POST, so a mail scanner that prefetches the link cannot spend the invitation
 * before its recipient has read the message. This screen is what the GET
 * renders: the SPA shell, served by the platform's not-found handler for any
 * path that is not an API route.
 *
 * **Signing in comes first, and carries the invitation with it.** The person
 * being invited usually has no account here yet, so a bare "sign in" that
 * dropped them at the sites screen would end the flow at the moment it started.
 * The sign-in link therefore names this exact path as its `returnTo`, which
 * `/auth/login` seals into the handshake cookie: the value never reaches Auth0
 * and is not something a redirect can be talked into.
 *
 * **The token is never shown, logged, or put in a message.** It is in the URL
 * because that is what an emailed link is, and it goes from there into one POST
 * body. Every refusal, expired, spent, wrong address, never existed, is the one
 * sentence the platform returns, so a token holder cannot probe which of those
 * it was.
 *
 * **The wrong account has a way out of it (LGN-131).** This screen renders
 * before the shell does, so there is no header here and no account menu: the
 * copy used to tell somebody signed in as the wrong person to sign out, on a
 * page with nothing to sign out with. It now names the address they are signed
 * in as, and offers a link that revokes the session and starts sign-in again
 * with this same invitation as its `returnTo`. What it still does not do is say
 * anything about the invitation itself. The refusal stays the platform's one
 * uniform sentence, because naming the organisation or the invited address
 * would hand a token holder the membership fact the uniformity exists to hide.
 *
 * The layout is the library's splash panel, the same one sign-in and the
 * visitor gate use. Somebody arriving here has never seen this product before,
 * and the first page they meet should look like the one they will sign in on.
 */
export function AcceptInvitation(props: {
  token: string;
  signedIn: boolean;
  branding: Branding;
  /** The signed-in address, as `/api/auth/me` gave it, or null when nobody is. */
  email: string | null;
  navigate: (to: string) => void;
  onAccepted: (orgId: string) => void;
}): React.ReactElement {
  const accept = useMutation({
    mutationFn: () => api.acceptInvitation(props.token),
    onSuccess: (result) => {
      // Switch to the org they just joined: they followed a link about it, so
      // landing anywhere else is a small betrayal of what they asked for.
      props.onAccepted(result.orgId);
    },
  });

  /**
   * Where sign-in comes back to, for the signed-out visitor and for somebody
   * signed in as the wrong person alike.
   *
   * `/invitations/<token>` survives the platform's `safeReturnTo` as it
   * stands: one leading slash, no control characters, and the only denied
   * prefix on that route is `/auth/`. The token is base64url, so the encoding
   * here touches nothing but the separators.
   */
  const loginHref = `/auth/login?returnTo=${encodeURIComponent(`/invitations/${props.token}`)}`;

  /**
   * The same destination, for somebody signed in as the wrong person (LGN-141).
   *
   * `prompt=login` is the difference, and without it this link did not recover
   * anything: revoking the Lagoon session leaves Auth0's own session standing,
   * so the next `/authorize` silently re-issued the identity the person was
   * trying to get away from and returned them to the same refusal. The
   * parameter is what makes Auth0 ask again, and `/auth/login` honours only
   * this one value, so it cannot be turned into `prompt=none`.
   *
   * Not on `loginHref` itself: the signed-out visitor above has no session to
   * get past, and re-prompting somebody who has not signed in yet is a step
   * for nothing.
   */
  const switchHref = `${loginHref}&prompt=login`;

  const [switching, setSwitching] = useState(false);

  /**
   * Leave this account and come back to this invitation (LGN-131).
   *
   * Sign-out first, because `/auth/login` with a live session would return the
   * same person to the same refusal. Best-effort, exactly as the shell's own
   * sign-out is: the session row is revoked server-side, and a failed request
   * must not strand somebody on a screen whose only other exit is the one that
   * did not work. The navigation is a real page load either way, because
   * `/auth/login` is a server route rather than one of this router's.
   */
  const signInAsSomeoneElse = useCallback(() => {
    setSwitching(true);
    void api
      .signOut()
      .catch(() => {})
      .finally(() => {
        window.location.href = switchHref;
      });
  }, [switchHref]);

  return (
    <SplashLayout
      brand={<LagoonLockup product={props.branding.productName} />}
      title="You have been invited"
    >
      <div className="lg:flex lg:flex-col lg:gap-5">
        {!props.signedIn ? (
          <>
            <Text size="sm" tone="muted">
              Sign in with the address the invitation was sent to, and we will bring you straight
              back here.
            </Text>
            {/*
              A real link, and a full page load on purpose: /auth/login is a
              server route that starts an OAuth handshake, not a client one.
              The returnTo is this same path, so the round trip ends where it
              began.
            */}
            <a className={buttonVariants({ block: true })} href={loginHref}>
              Sign in to accept
            </a>
          </>
        ) : accept.isSuccess ? (
          <>
            <Notice tone="success">
              {accept.data.alreadyMember
                ? `You were already in ${accept.data.orgName}.`
                : `You have joined ${accept.data.orgName} as ${roleWord(accept.data.role)}.`}
            </Notice>
            <Button
              block
              onClick={() => {
                props.navigate("/");
              }}
            >
              Go to sites
            </Button>
          </>
        ) : (
          <>
            {/*
              The single most useful fact on this screen, and the one it never
              said: which account is about to accept. `null` is the impossible
              case, `signedIn` is `me` having succeeded, and a bare "Signed in
              as" with nothing after it would be worse than silence.
            */}
            {props.email === null ? null : (
              <Text size="sm" tone="muted">
                Signed in as <Mono className="lg:normal-case lg:text-strong">{props.email}</Mono>
              </Text>
            )}

            <Text size="sm" tone="muted">
              Accepting adds you to the organisation that invited you. The address on the invitation
              has to match the one you are signed in with, so if it was meant for somebody else,
              sign in as them and follow the link again.
            </Text>

            {accept.isError ? (
              <Failure error={accept.error} fallback="That invitation could not be accepted." />
            ) : null}

            <div className="lg:flex lg:flex-wrap lg:gap-3">
              <Button
                disabled={accept.isPending || switching}
                onClick={() => {
                  accept.mutate();
                }}
              >
                {accept.isPending ? "Accepting…" : "Accept invitation"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  props.navigate("/");
                }}
              >
                Not now
              </Button>
            </div>

            {/*
              A real link as well as a handler: it is where somebody in the
              wrong account is going, so it should be inspectable, openable and
              readable as a destination. The click is intercepted only to
              revoke this session on the way past.
            */}
            <a
              className={buttonVariants({ variant: "link", size: "sm" })}
              href={switchHref}
              onClick={(event) => {
                event.preventDefault();
                signInAsSomeoneElse();
              }}
            >
              {switching ? "Signing out…" : "Sign in as someone else"}
            </a>
          </>
        )}
      </div>
    </SplashLayout>
  );
}

/**
 * The role as it reads inside "you have joined Acme as ___".
 *
 * Spelled out rather than derived from `ROLE_LABELS`, because the article is
 * not the only thing that changes: "a billing" is not English, and the seat is
 * called the billing contact everywhere a person is likely to hear about it.
 */
const ROLE_PHRASES: Readonly<Record<OrgRole, string>> = {
  owner: "an owner",
  admin: "an admin",
  member: "a member",
  billing: "the billing contact",
};

function roleWord(role: OrgRole): string {
  return ROLE_PHRASES[role];
}
